/**
 * Token Manager - Client-Side Dual Token System
 *
 * This service manages access and refresh tokens for the application.
 * It automatically refreshes access tokens and handles token expiration.
 *
 * Features:
 * - Automatic access token refresh
 * - In-memory access token storage
 * - HttpOnly cookie refresh tokens
 * - Concurrent request handling during refresh
 * - Graceful fallback to localStorage (during migration)
 */

import { API_BASE } from '../config/api';

class TokenManager {
  constructor() {
    this.accessToken = null;
    this.refreshInProgress = false;
    this.refreshPromise = null;
    this.enabled = false; // AuthContext enables it when REACT_APP_DUAL_TOKEN_ENABLED=true (production)

    // Bumped by signOut. A refresh that started under an older generation
    // throws its result away instead of signing the tab back in (BH-011).
    this.authGeneration = 0;
    this.signingOut = false;

    // Configuration - use centralized API_BASE
    this.apiBase = API_BASE;

    this.refreshThresholdMs = 5 * 60 * 1000; // Refresh when < 5 minutes left

    // Callbacks for auth state changes
    this.onAuthStateChange = null;
    this.onTokenExpired = null;
  }

  // =============================================================================
  // CONTROL METHODS
  // =============================================================================

  enable() {
    this.enabled = true;
    console.log('🔄 TokenManager enabled - using dual token system');

    // Clear any existing localStorage tokens when switching to new system
    this.clearLegacyTokens();
  }

  disable() {
    this.enabled = false;
    console.log('🔄 TokenManager disabled - using legacy token system');
    this.accessToken = null;
  }

  isEnabled() {
    return this.enabled;
  }

  clearLegacyTokens() {
    localStorage.removeItem('authToken');
    sessionStorage.removeItem('authToken');
    console.log('🧹 Cleared legacy auth tokens from localStorage');
  }

  // Legacy single-token storage. "Remember me" picks the store: localStorage
  // survives browser restarts, sessionStorage ends with the browser session.
  _getLegacyToken() {
    return localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
  }

  // =============================================================================
  // TOKEN MANAGEMENT
  // =============================================================================

  /**
   * Synchronous token access — the primary method for getting a token.
   * - When disabled: reads from localStorage (legacy single-token system)
   * - When enabled: returns in-memory access token (set by sign-in or refresh)
   *
   * If the token is expired, the next API call will get a 401 and
   * makeAuthenticatedRequest() handles the refresh-and-retry.
   */
  getTokenSync() {
    if (!this.enabled) {
      return this._getLegacyToken();
    }
    return this.accessToken;
  }

  async getValidAccessToken() {
    // During migration period, fall back to old system
    if (!this.enabled) {
      return this._getLegacyToken();
    }

    // If no access token, try to get one via refresh
    if (!this.accessToken) {
      await this.refreshAccessToken();
    }

    // If token exists, check if it needs refresh
    if (this.accessToken && this.isTokenExpiringSoon(this.accessToken)) {
      await this.refreshAccessToken();
    }

    return this.accessToken;
  }

  setAccessToken(token) {
    this.accessToken = token;

    // Notify listeners of auth state change
    if (this.onAuthStateChange) {
      this.onAuthStateChange(!!token);
    }
  }

  clearAccessToken() {
    this.accessToken = null;

    if (this.onAuthStateChange) {
      this.onAuthStateChange(false);
    }
  }

  /**
   * Store a token after login/signup — handles both systems.
   * Called by AuthService.signIn / signUp.
   *
   * remember=false keeps the token in sessionStorage so signing in lasts only
   * until the browser closes. Dual-token mode ignores it client-side — the
   * server owns the refresh cookie's lifetime: signin-v2 reads `remember`
   * from the request body and, when it is false, sets a browser-session
   * cookie that refresh rotation keeps one (BH-02-03).
   */
  storeToken(token, remember = true) {
    if (this.enabled) {
      // Signup lands here: a refresh still in flight from before it must not
      // clear the new session when it fails (see signIn).
      this.authGeneration++;
      this.setAccessToken(token);
    } else {
      const store = remember ? localStorage : sessionStorage;
      const other = remember ? sessionStorage : localStorage;
      store.setItem('authToken', token);
      other.removeItem('authToken'); // never leave two live tokens behind
    }
  }

  /**
   * Clear stored token on logout — handles both systems.
   * Called by AuthService.signOut.
   */
  clearToken() {
    if (this.enabled) {
      this.clearAccessToken();
    } else {
      localStorage.removeItem('authToken');
      sessionStorage.removeItem('authToken');
    }
  }

  isTokenExpiringSoon(token) {
    try {
      // Decode JWT without verification (we just need the expiry)
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expiryTime = payload.exp * 1000; // Convert to milliseconds
      const now = Date.now();

      return (expiryTime - now) < this.refreshThresholdMs;
    } catch (error) {
      console.warn('Failed to decode token expiry:', error);
      return true; // Assume expired if we can't decode
    }
  }

  // =============================================================================
  // AUTHENTICATION METHODS
  // =============================================================================

  async signIn(email, password, remember = true) {
    // A refresh still in flight from before this sign-in (the page-load one)
    // must not clear or overwrite the new session when it lands.
    this.authGeneration++;
    try {
      const response = await fetch(`${this.apiBase}/api/auth/${this.enabled ? 'signin-v2' : 'signin'}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Include cookies
        body: JSON.stringify({ email, password, remember }),
      });

      const data = await response.json();

      if (!response.ok) {
        return { success: false, error: data.message || 'Login failed' };
      }

      if (this.enabled) {
        // New dual token system
        this.setAccessToken(data.accessToken);
        // Refresh token is automatically set as HttpOnly cookie
      } else {
        // Legacy system
        this.storeToken(data.token, remember);
      }

      return {
        success: true,
        user: data.user,
        message: data.message
      };

    } catch (error) {
      console.error('Sign in error:', error);
      return { success: false, error: 'Network error during sign in' };
    }
  }

  async signOut() {
    // A refresh in flight rotates the refresh cookie. Let it land first so the
    // sign-out below carries (and revokes) the current cookie, and discard its
    // access token — and any refresh that starts while we sign out (BH-011).
    this.signingOut = true;
    this.authGeneration++;
    if (this.refreshPromise) {
      try { await this.refreshPromise; } catch (e) { /* signing out anyway */ }
    }

    try {
      const endpoint = this.enabled ? 'signout-v2' : 'signout';

      await fetch(`${this.apiBase}/api/auth/${endpoint}`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        credentials: 'include',
      });

      // Clear tokens regardless of response
      if (this.enabled) {
        this.clearAccessToken();
        // HttpOnly cookie will be cleared by server
      } else {
        this.clearLegacyTokens();
      }

      return { success: true };

    } catch (error) {
      console.error('Sign out error:', error);

      // Clear local tokens even if server request failed
      if (this.enabled) {
        this.clearAccessToken();
      } else {
        this.clearLegacyTokens();
      }

      return { success: true }; // Always return success for logout
    } finally {
      this.authGeneration++;
      this.signingOut = false;
    }
  }

  async refreshAccessToken() {
    // Never sign back in while signing out
    if (this.signingOut) {
      return null;
    }

    // Prevent concurrent refresh requests
    if (this.refreshInProgress) {
      return this.refreshPromise;
    }

    this.refreshInProgress = true;
    this.refreshPromise = this.performRefresh();

    try {
      const result = await this.refreshPromise;
      return result;
    } finally {
      this.refreshInProgress = false;
      this.refreshPromise = null;
    }
  }

  async performRefresh() {
    const generation = this.authGeneration;
    try {
      const response = await fetch(`${this.apiBase}/api/auth/refresh-v2`, {
        method: 'POST',
        credentials: 'include', // Include refresh token cookie
        headers: {
          'Content-Type': 'application/json',
        },
      });

      // Signed out while this was in flight: the result belongs to the old session
      if (generation !== this.authGeneration) {
        return null;
      }

      if (!response.ok) {
        // Refresh failed - user needs to log in again
        this.clearAccessToken();

        if (this.onTokenExpired) {
          // `rejected`: the server refused the session (401) — as opposed to
          // a 5xx or a rate limit, where the session may well still be good.
          this.onTokenExpired({ rejected: response.status === 401 });
        }

        return null;
      }

      const data = await response.json();
      if (generation !== this.authGeneration) {
        return null;
      }
      this.setAccessToken(data.accessToken);

      return data.accessToken;

    } catch (error) {
      if (generation !== this.authGeneration) {
        return null;
      }
      console.error('Token refresh error:', error);
      this.clearAccessToken();

      if (this.onTokenExpired) {
        // A network failure (a laptop waking before Wi-Fi is back) is not a
        // rejected session.
        this.onTokenExpired({ rejected: false });
      }

      return null;
    }
  }

  // =============================================================================
  // UTILITY METHODS
  // =============================================================================

  getAuthHeaders() {
    const token = this.getTokenSync();
    if (!token) return {};
    return { 'Authorization': `Bearer ${token}` };
  }

  async makeAuthenticatedRequest(url, options = {}) {
    // Refuse to send the Bearer token anywhere but our own backend
    const urlStr = typeof url === 'string' ? url : url.toString();
    if (!urlStr.startsWith('/')) {
      const target = new URL(urlStr, window.location.origin);
      const apiOrigin = new URL(this.apiBase, window.location.origin).origin;
      if (target.origin !== window.location.origin && target.origin !== apiOrigin) {
        throw new Error('makeAuthenticatedRequest only supports same-origin/API URLs');
      }
    }

    const token = await this.getValidAccessToken();

    if (!token) {
      throw new Error('No valid access token available');
    }

    const response = await fetch(url, {
      ...options,
      credentials: 'include',
      headers: {
        ...options.headers,
        'Authorization': `Bearer ${token}`,
      },
    });

    // If request fails with 401, try to refresh token and retry
    if (response.status === 401 && this.enabled) {
      const newToken = await this.refreshAccessToken();

      if (newToken) {
        // Retry the request with new token
        return fetch(url, {
          ...options,
          credentials: 'include',
          headers: {
            ...options.headers,
            'Authorization': `Bearer ${newToken}`,
          },
        });
      }
    }

    return response;
  }

  isAuthenticated() {
    if (this.enabled) {
      return !!this.accessToken;
    } else {
      return !!this._getLegacyToken();
    }
  }

  // =============================================================================
  // EVENT HANDLERS
  // =============================================================================

  onAuthStateChanged(callback) {
    this.onAuthStateChange = callback;
  }

  onTokenExpiredCallback(callback) {
    this.onTokenExpired = callback;
  }

  // =============================================================================
  // DEVELOPMENT UTILITIES
  // =============================================================================

  getDebugInfo() {
    return {
      enabled: this.enabled,
      hasAccessToken: !!this.accessToken,
      hasLegacyToken: !!this._getLegacyToken(),
      refreshInProgress: this.refreshInProgress,
      apiBase: this.apiBase
    };
  }

  // Test the token refresh mechanism (development only)
  async testRefresh() {
    if (!this.enabled) {
      console.log('TokenManager not enabled - cannot test refresh');
      return;
    }

    console.log('🧪 Testing token refresh...');
    const result = await this.refreshAccessToken();
    console.log('🧪 Refresh result:', result ? 'Success' : 'Failed');
    return result;
  }
}

// Export singleton instance
const tokenManager = new TokenManager();
export default tokenManager;